import React, { useEffect } from 'react';

import { Platform, StatusBar } from 'react-native';

import ENV from 'react-native-config';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { configureReanimatedLogger } from 'react-native-reanimated';
import {
    SafeAreaProvider,
    initialWindowMetrics,
} from 'react-native-safe-area-context';
import { Provider } from 'react-redux';

import { BottomSheetModalProvider } from '@gorhom/bottom-sheet';
import * as Sentry from '@sentry/react-native';
import { PersistGate } from 'redux-persist/integration/react';
import store, { persistor } from 'store';

import gStyles from 'config/styles';

import NavigatorContainer from 'router/NavigatorContainer';

import { setInitialWebPathName } from 'store/config/actions';
import { startSentryTransaction } from 'store/sentry/actions';
import { SENTRY_TRANSACTIONS } from 'store/sentry/constants';
import { setAppSessionId } from 'store/session/actions';

import { requestTrackingPermission } from 'services/permissions';

import { onSentryBeforeSend } from 'utils/axios/nagra/utils';
import { lockToDefaultOrientation } from 'utils/orientation';
import { isAndroid15, isWeb } from 'utils/responsiveness';
import { getUniqueId } from 'utils/uniqueId';

import NoInternetAlert from 'components/NoInternetAlert';
import ReturnToLauncherListener from 'components/ReturnToLauncherListener';
import SplashView from 'components/SplashView';

if (isWeb) {
    Sentry.init({
        dsn: __DEV__ ? '' : ENV.SENTRY_DSN,
        // @ts-ignore
        integrations: [Sentry.browserTracingIntegration()],
        tracesSampleRate: 0.2,
        beforeSend: onSentryBeforeSend,
    });
} else {
    Sentry.init({
        dsn: __DEV__ ? '' : ENV.SENTRY_DSN,
        tracesSampleRate: 0.2,
        integrations: [Sentry.reactNativeTracingIntegration()],
        enableNative: true,
        enableTracing: true,
        beforeSend: onSentryBeforeSend,
        appHangTimeoutInterval: 10,
    });
}

configureReanimatedLogger({
    strict: false,
});

const App = () => {
    const storeState = store.getState();

    useEffect(() => {
        requestTrackingPermission();

        if (Platform.OS === 'web' && window.location.pathname !== '/') {
            store.dispatch(
                setInitialWebPathName({
                    pathName: window.location.pathname.replace('/', ''),
                    query: window.location.search.replace('?', ''),
                }),
            );
        }

        if (Platform.OS !== 'web') {
            lockToDefaultOrientation();
        }

        store.dispatch(
            startSentryTransaction(
                SENTRY_TRANSACTIONS.AppStartToProfileSelection,
            ),
        );
        store.dispatch(
            startSentryTransaction(SENTRY_TRANSACTIONS.AppStartToWelcomePage),
        );
        store.dispatch(setAppSessionId(getUniqueId()));
    }, []);

    return (
        <Provider store={store}>
            <PersistGate loading={null} persistor={persistor}>
                <SafeAreaProvider
                    style={
                        isAndroid15 &&
                        !storeState.player.fullscreen && {
                            marginBottom: initialWindowMetrics?.insets.bottom,
                        }
                    }
                >
                    <StatusBar />
                    <GestureHandlerRootView style={gStyles.flex}>
                        <BottomSheetModalProvider>
                            <NavigatorContainer />
                        </BottomSheetModalProvider>
                    </GestureHandlerRootView>
                    <NoInternetAlert />
                </SafeAreaProvider>
            </PersistGate>
            <ReturnToLauncherListener />
            <SplashView />
        </Provider>
    );
};

export default isWeb ? App : Sentry.wrap(App);
